'use client';

import React, {
  useEffect,
  useRef,
  useState,
  FormEvent,
  ChangeEvent,
} from 'react';
import { useInView } from 'react-intersection-observer';

const CustomStyles = () => {
  const customStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');

    .custom-checkbox {
      appearance: none;
      cursor: pointer;
      transition: all 0.15s ease-in-out;
      /* Изначальный прозрачный фон, как у GlassCard, с тонкой белой рамкой */
      background-color: rgba(255,255,255,.08); 
      border: 2px solid rgba(255,255,255,.15); /* Более заметная рамка */
      border-radius: 0.25rem; /* Задаем радиус для скругления */
      flex-shrink: 0; /* Чтобы чекбокс не сжимался при недостатке места */
    }

    .custom-checkbox:checked {
      background-color: #FFC300; /* Оранжевый фон при выборе */
      border-color: #FFC300;     /* Оранжевая рамка при выборе */
      /* Используем темный цвет для галочки (#102a43) */
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23102a43' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
      background-size: 80%; 
      background-repeat: no-repeat;
      background-position: center;
    }
  `;
  return <style dangerouslySetInnerHTML={{ __html: customStyles }} />;
};

const aboutData = {
  chips: [
    { href: '#mission', label: 'Миссия' },
    { href: '#numbers', label: 'Цифры' },
    { href: '#team', label: 'Команда' },
    { href: '#timeline', label: 'История' },
    { href: '#values', label: 'Ценности' },
    { href: '#press', label: 'Пресс-кит' },
    { href: '#careers', label: 'Карьера' },
    { href: '#contact', label: 'Контакты' },
  ],
  counters: [
    { to: 96, label: 'средняя явка персонала' },
    { to: 19, label: 'снижение CPH, %' },
    { to: 24, label: 'SLA на замену, ч' },
    { to: 180, label: 'хайров за квартал' },
  ],
  values: [
    {
      title: 'Прозрачность',
      description: 'SLA, метрики, прогнозы и честные статусы кандидатов.',
    },
    {
      title: 'Скорость',
      description: 'Поток кандидатов и быстрая связь с HM/бизнесом.',
    },
    {
      title: 'Ответственность',
      description: 'Финансовая ответственность за срыв сроков/качества.',
    },
  ],
  team: [
    {
      name: 'Селима Керимова',
      role: 'CEO • Стратегия и партнёрства',
      bio: '12+ лет в HR и операционном менеджменте. Запускала RPO/MSP-направления.',
      photoUrl:
        'https://static.tildacdn.com/tild6637-3833-4334-b235-373164326332/user-female-circle-f.png',
    },
    {
      name: 'Магомед Исмаилов',
      role: 'COO • Операции и SLA',
      bio: 'Отвечает за воронку, прогноз и KPI. Стандартизирует процессы и отчётность.',
      photoUrl: 'https://avatars.githubusercontent.com/u/1608890?v=4?s=400',
    },
    {
      name: 'Марха Асланбекова',
      role: 'Head of Talent • Сорсинг и обучение',
      bio: 'Развивает команду рекрутёров, наставничество и стандарты интервью.',
      photoUrl:
        'https://static.tildacdn.com/tild6637-3833-4334-b235-373164326332/user-female-circle-f.png',
    },
  ],
  timeline: [
    {
      year: '2023 — Запуск INTERIM',
      description: 'Первые 50+ закрытий, формирование SLA-подхода.',
    },
    {
      year: '2024 — Масштабирование',
      description: 'RPO/MSP-направления, интеграции с ATS/BI.',
    },
    {
      year: '2025 — География и партнёрства',
      description: '12 регионов, федеральные контракты, кейсы с CPH −19%.',
    },
  ],
  press: [
    {
      title: 'Логотипы и гайдлайн',
      description: 'SVG/PNG, правила использования, цвета и шрифты.',
      href: '#',
    },
    {
      title: 'Фотографии и иллюстрации',
      description: 'Пресс-фото команды и сток-наборы.',
      href: '#',
    },
    {
      title: 'О компании (one-pager)',
      description: 'PDF для партнёров, журналистов и тендеров.',
      href: '#',
    },
  ],
  careers: [
    {
      title: 'Рекрутер (RPO)',
      description: 'Гибрид/remote · KPI и бонусы за закрытия.',
    },
    {
      title: 'Сорсер',
      description: 'Гибрид/remote · Автоматизация поиска, LinkedIn/HH.',
    },
    {
      title: 'Account / PM',
      description: 'Ведение портфеля клиентов, отчётность и SLA.',
    },
  ],
};

const GlassCard = ({ children, className }: React.PropsWithChildren<{ className?: string }>) => (
  <article
    className={`rounded-2xl p-6 ${className || ''}`}
    style={{
      background: 'rgba(255,255,255,.08)',
      backdropFilter: 'blur(8px)',
    }}
  >
    {children}
  </article>
);

const Chip = ({ href, children }: React.PropsWithChildren<{ href: string }>) => (
  <a
    href={href}
    className="px-3 py-1 rounded-full hover:bg-white/25 text-sm"
    style={{ background: 'rgba(255,255,255,.14)' }}
  >
    {children}
  </a>
);

const Counter = ({ to, label }: { to: number; label: string }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.4,
    rootMargin: '0px',
  });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;

    const duration = 900;
    const start = performance.now();
    let animationFrameId: number;

    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / duration);
      setCount(Math.floor(to * k));
      if (k < 1) {
        animationFrameId = requestAnimationFrame(tick);
      }
    };

    animationFrameId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animationFrameId);
  }, [to, inView]);

  return (
    <div
      ref={ref}
      className="rounded-2xl p-6 text-center"
      style={{
        background: 'rgba(255,255,255,.08)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div className="text-5xl font-extrabold">{count}</div>
      <div className="text-xs text-gray-300 mt-1">{label}</div>
    </div>
  );
};

const useContactForm = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    formName: 'О нас',
  });
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | 'consent_required' | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (submitStatus === 'consent_required') {
      setSubmitStatus(null);
    }
  };

  const handleConsentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked && submitStatus === 'consent_required') {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isConsentChecked) {
      setSubmitStatus('consent_required');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/about-page-requests`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ data: form }),
});

if (response.ok) {
  setSubmitStatus('success');
  setForm({ name: '', email: '', phone: '', company: '', message: '', formName: 'О нас' });
  setIsConsentChecked(false);
} else {
  setSubmitStatus('error');
}

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isConsentChecked,
    isSubmitting,
    submitStatus,
    handleInputChange,
    handleConsentChange,
    handleSubmit,
  };
};

export default function AboutPage() {
  const {
    form,
    isConsentChecked,
    isSubmitting,
    submitStatus,
    handleInputChange,
    handleConsentChange,
    handleSubmit
  } = useContactForm();

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <CustomStyles />
      <header
        className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{
          background:
            'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)',
        }}
      >
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            {aboutData.chips.map((chip) => (
              <Chip key={chip.label} href={chip.href}>
                {chip.label}
              </Chip>
            ))}
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
            INTERIM — команда, которая ускоряет найм
          </h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Мы помогаем бизнесу быстро закрывать вакансии и строить
            процессы под рост, а людям — находить работу мечты.
            Прозрачные SLA, цифры и ответственность за результат.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="#contact"
              className="px-4 py-3 rounded-xl font-semibold"
              style={{ background: '#FFC300', color: '#102a43' }}
            >
              Связаться с нами
            </a>
            <a
              href="#numbers"
              className="px-4 py-3 rounded-xl font-semibold inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base"
            >
              Смотреть факты
            </a>
          </div>
        </div>
      </header>
      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="mission" className="grid gap-6 md:grid-cols-3">
          <GlassCard className="md:col-span-2">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Наша миссия
            </h2>
            <p className="text-gray-200">
              Делать найм предсказуемым и честным: для компаний — по
              срокам, качеству и бюджету; для кандидатов —
              по прозрачной обратной связи, развитию и справедливой
              оффер-ставке.
            </p>
            <ul className="mt-4 grid gap-3 md:grid-cols-2 text-sm text-gray-200">
              <li className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.08)' }}>
                • SLA на каждый этап воронки
              </li>
              <li className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.08)' }}>
                • Интеграции с ATS/HRIS/BI
              </li>
              <li className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.08)' }}>
                • Обучение Hiring-менеджеров
              </li>
              <li className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.08)' }}>
                • Карьерные сервисы для соискателей
              </li>
            </ul>
          </GlassCard>
          <GlassCard>
            <h3 className="text-xl font-semibold mb-2">Фокус</h3>
            <p className="text-sm text-gray-200">
              Ритейл, e-commerce, логистика, клиентский сервис,
              HR/рекрутинг, IT-поддержка, аналитика.
            </p>
            <hr className="my-4 border-white/10" />
            <h3 className="text-xl font-semibold mb-2">География</h3>
            <p className="text-sm text-gray-200">
              Работаем по РФ/СНГ и remote-форматам.
            </p>
          </GlassCard>
        </section>
        <section id="numbers">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            INTERIM в цифрах
          </h2>
          <div className="grid gap-6 md:grid-cols-4">
            {aboutData.counters.map((counter) => (
              <Counter key={counter.label} to={counter.to} label={counter.label} />
            ))}
          </div>
        </section>
        <section id="values">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ценности
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {aboutData.values.map((v) => (
              <GlassCard key={v.title}>
                <h3 className="text-xl font-semibold mb-2">{v.title}</h3>
                <p className="text-sm text-gray-200">{v.description}</p>
              </GlassCard>
            ))}
          </div>
        </section>
        <section id="team">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Лидершип-команда
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {aboutData.team.map((member) => (
              <GlassCard key={member.name}>
                <div className="flex items-center gap-4">
                  <img
                    src={member.photoUrl}
                    alt={`Фотография ${member.name}`}
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                  <div>
                    <h3 className="text-lg font-semibold">{member.name}</h3>
                    <p className="text-sm text-gray-300">{member.role}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-200 mt-4">{member.bio}</p>
              </GlassCard>
            ))}
          </div>
        </section>
        <section id="timeline">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Короткая история
          </h2>
          <ol className="relative border-l border-white pl-6 space-y-6">
            {aboutData.timeline.map((item) => (
              <li key={item.year}>
                <h3 className="font-semibold">{item.year}</h3>
                <p className="text-sm text-gray-200">{item.description}</p>
              </li>
            ))}
          </ol>
        </section>
        <section id="press">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Пресс-кит и материалы бренда
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {aboutData.press.map((item) => (
              <a
                key={item.title}
                href={item.href}
                className="rounded-2xl p-6 hover:bg-white/10"
                style={{
                  background: 'rgba(255,255,255,.08)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-sm text-gray-200 mt-1">{item.description}</p>
              </a>
            ))}
          </div>
        </section>
        <section id="careers">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Карьера в INTERIM
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {aboutData.careers.map((career) => (
              <GlassCard key={career.title}>
                <h3 className="text-xl font-semibold">{career.title}</h3>
                <p className="text-sm text-gray-200 mt-1">{career.description}</p>
                <a
                  href="#contact"
                  className="mt-4 inline-block px-4 py-2 rounded-xl font-semibold bg-white/10 hover:bg-white/20 transition-colors duration-200"
                >
                  Откликнуться
                </a>
              </GlassCard>
            ))}
          </div>
        </section>
        <section id="contact">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Связаться с нами
          </h2>
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl p-6 grid gap-4 md:grid-cols-2"
            style={{
              background: 'rgba(255,255,255,.08)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <input
              name="name"
              value={form.name}
              onChange={handleInputChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Имя"
              required
            />
            <input
              name="email"
              value={form.email}
              onChange={handleInputChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="E-mail"
              type="email"
              required
            />
            <input
              name="phone"
              value={form.phone}
              onChange={handleInputChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Телефон"
              type="tel"
              required
            />
            <input
              name="company"
              value={form.company}
              onChange={handleInputChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Компания (по желанию)"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={handleInputChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Коротко опишите вопрос/задачу"
              required
            ></textarea>

            <div className="md:col-span-2 flex items-center justify-start">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  checked={isConsentChecked}
                  onChange={handleConsentChange}
                  className="w-5 h-5 custom-checkbox"
                />
                <span className="text-sm text-gray-200">
                  Я соглашаюсь с <a href="/privacy" className="underline hover:text-white">обработкой персональных данных</a>
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="rounded-xl px-4 py-3 font-semibold md:col-span-2"
              style={{ background: '#FFC300', color: '#102a43' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </button>

            {submitStatus === 'consent_required' && (
              <div className="md:col-span-2 text-center text-sm text-white">
                Пожалуйста, подтвердите согласие на обработку персональных данных.
              </div>
            )}
            {submitStatus === 'success' && (
              <div className="md:col-span-2 text-center text-sm text-green-300">
                ✅ Ваша заявка успешно отправлена!
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                ❌ Ошибка при отправке. Пожалуйста, попробуйте снова.
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}